import { Image, Spinner } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { getResidenceTypeImage } from "../clients/BackOfficeApi";
import { atom, useAtom } from "jotai";

export const cachedImagesAtom = atom<Record<number, string>>({});
const requestImagePromises: Record<number, Promise<string>> = {};

interface ResidenceTypeImageProps {
  id: number;
}

export function ResidenceTypeImage({ id }: ResidenceTypeImageProps) {
  const [imageUrl, setImageUrl] = useState<string>();
  const [cachedImages, setCachedImages] = useAtom(cachedImagesAtom);

  useEffect(() => setImageUrl(cachedImages[id]), [id, cachedImages]);

  const loadImage = useCallback(() => {
    requestImagePromises[id] ??= getResidenceTypeImage({
      path: { residenceTypeId: id },
      parseAs: "stream",
    })
      .then((result) => result.response.blob())
      .then((blob) => URL.createObjectURL(blob))
      .catch((error) => {
        throw new Error(`Failed to load image for ID ${id}: ${error.message}`);
      })
      .finally(() => delete requestImagePromises[id]);

    return requestImagePromises[id];
  }, [id]);

  useEffect(() => {
    if (cachedImages.hasOwnProperty(id)) return;

    const abortController = new AbortController();

    loadImage().then((url) => {
      setCachedImages((prev) => ({ ...prev, [id]: url }));

      if (!abortController.signal.aborted) setImageUrl(url);
    });

    return () => abortController.abort("Component unmounted or ID changed");
  }, [id, loadImage, cachedImages, setCachedImages]);

  if (!imageUrl) return <Spinner color="brand.blue.500" size="md" />;

  return (
    <Image
      bg="brand.gray.100"
      borderRadius={8}
      h="100px"
      loading="lazy"
      mr={4}
      src={imageUrl}
      w="100px"
    />
  );
}
